import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

// Material UI
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import { alpha, useTheme } from "@mui/material/styles";

// Icons
import PaidIcon from "@mui/icons-material/Paid";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SearchIcon from "@mui/icons-material/Search";

// Components
import NamedRow from "../rows/namedRow";
import {
  AnimatedModal,
  ModalContent,
  ModalHeader,
} from "../modals/AnimatedModal";
import RemoveModal from "../modals/removeModal";
import CreateOrEditDeliveryOperatorModal from "./deliveryOperators/CreateOrEditDeliveryOperatorModal";
import OperatorFeeModal from "./deliveryOperators/OperatorFeeModal";

// Styled components
import {
  Panel,
  PanelRow,
  SectionBar,
  SectionBarLabel,
  RowLabel,
  RowValue,
  PanelActionBtn,
} from "../styled/panel";
import { LightGreyBox } from "../styled";
// Services & Utils
import { isOwner } from "../../common/validators";
import api from "../../store/services/api";
import { dispatchSnackBarMessage } from "../../utils/snackbar";
import DeliveryOperatorService, {
  deleteDeliveryOperator,
} from "../../api/deliveryOperator";

// Animation variants — app-standard spring (stiffness 300, damping 24)
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.07 },
  },
};

export default function OperatorsModal({
  show,
  closeModal,
  saloonFee,
  counterFee,
}) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);

  const [deliveryOperators, setDeliveryOperators] = useState([]);
  const [editingOperator, setEditingOperator] = useState(null);
  const [showCreateOperatorModal, setShowCreateOperatorModal] = useState(false);
  const [showOperatorFeeModal, setShowOperatorFeeModal] = useState(false);
  const [operatorToDelete, setOperatorToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (show) {
      loadDeliveryOperators();
    }
  }, [show]);

  const loadDeliveryOperators = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/user/operators/");
      setDeliveryOperators(data);
    } catch (e) {
      const message =
        e.response?.data || "Erro ao carregar operadores de entrega.";
      setErrorMessage(message);
      setShowError(true);
      dispatchSnackBarMessage(`operators-list-${new Date()}`, message, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCreateOperatorModal = useCallback(() => {
    setShowCreateOperatorModal((prev) => !prev);
    setEditingOperator(null);
  }, []);

  const createDeliveryOperator = useCallback(
    async (operatorObj) => {
      try {
        setLoading(true);
        handleCreateOperatorModal();
        await DeliveryOperatorService.createDeliveryOperator(operatorObj);

        dispatchSnackBarMessage(
          `operators-list-${new Date()}`,
          "Operador criado com sucesso",
          "success"
        );
        await loadDeliveryOperators();
      } catch (e) {
        const message =
          e.response?.data?.message || "Erro ao cadastrar membro da equipe";
        dispatchSnackBarMessage(
          `operators-list-${new Date()}`,
          message,
          "error"
        );
      } finally {
        setLoading(false);
      }
    },
    [handleCreateOperatorModal, loadDeliveryOperators]
  );

  const handleEditOperator = useCallback((operator) => {
    setEditingOperator(operator);
    setShowCreateOperatorModal(true);
  }, []);

  const editDeliveryOperator = useCallback(
    async (operatorObj) => {
      try {
        setLoading(true);
        await DeliveryOperatorService.editDeliveryOperator(
          editingOperator.id,
          operatorObj
        );
        setEditingOperator(null);

        dispatchSnackBarMessage(
          `operators-list-${new Date()}`,
          "Operador editado com sucesso",
          "success"
        );
      } catch (e) {
        const message =
          e.response?.data?.message || "Erro ao editar membro da equipe";
        dispatchSnackBarMessage(
          `operators-list-${new Date()}`,
          message,
          "error"
        );
      } finally {
        setLoading(false);
        handleCreateOperatorModal();
        await loadDeliveryOperators();
      }
    },
    [editingOperator, handleCreateOperatorModal, loadDeliveryOperators]
  );

  const handleDeleteOperatorModal = useCallback((operator = null) => {
    setOperatorToDelete(operator);
    setShowDeleteModal((prev) => !prev);
  }, []);

  const handleDeleteOperator = useCallback(async () => {
    try {
      await deleteDeliveryOperator(operatorToDelete.id);
      handleDeleteOperatorModal();
      await loadDeliveryOperators();

      dispatchSnackBarMessage(
        `operator-deleted-${new Date()}`,
        "Operador removido com sucesso",
        "success"
      );
    } catch (error) {
      dispatchSnackBarMessage(
        `operator-delete-error-${new Date()}`,
        "Erro ao remover operador",
        "error"
      );
    }
  }, [operatorToDelete, handleDeleteOperatorModal, loadDeliveryOperators]);

  const handleOperatorFeeModal = useCallback(() => {
    setShowOperatorFeeModal((prev) => !prev);
  }, []);

  // Filter operators based on search term
  const filteredOperators = useMemo(() => {
    if (!searchTerm) return deliveryOperators;
    return deliveryOperators.filter((operator) => {
      const fullName =
        `${operator.first_name} ${operator.last_name}`.toLowerCase();
      const username = operator.username.toLowerCase();
      const searchLower = searchTerm.toLowerCase();
      return fullName.includes(searchLower) || username.includes(searchLower);
    });
  }, [deliveryOperators, searchTerm]);

  const renderedOperators = useMemo(
    () =>
      filteredOperators.map((operator, index) => (
        <NamedRow
          key={operator.id}
          id={operator.id}
          index={index + 1}
          name={`${operator.first_name} ${operator.last_name} - ${operator.username}`}
          type={operator.type}
          editAction={() => handleEditOperator(operator)}
          deleteAction={() => handleDeleteOperatorModal(operator)}
          disableActions={isOwner(operator.type)}
        />
      )),
    [filteredOperators, handleEditOperator, handleDeleteOperatorModal]
  );

  return (
    <AnimatedModal
      isOpen={show}
      onClose={closeModal}
      width="80%"
      maxWidth="900px"
    >
      <ModalHeader
        title="Gerenciamento de Equipe"
        icon={PeopleAltIcon}
        onClose={closeModal}
      />
      <ModalContent>
        <AnimatePresence>
          {showError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <Alert
                severity="error"
                onClose={() => setShowError(false)}
                sx={{ mb: 2, borderRadius: 1.5 }}
              >
                {errorMessage}
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Configuration & actions ──────────────────────────────── */}
        {loading ? (
          <Box sx={{ mb: 3 }}>
            <Skeleton
              variant="rectangular"
              height={120}
              sx={{ borderRadius: 1.5 }}
            />
          </Box>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Panel sx={{ mb: 3 }}>
              <SectionBar>
                <SectionBarLabel>Ações Rápidas</SectionBarLabel>
                <Box sx={{ display: "flex", gap: 0.75 }}>
                  <PanelActionBtn
                    size="small"
                    startIcon={<PersonAddIcon sx={{ fontSize: 14 }} />}
                    onClick={handleCreateOperatorModal}
                  >
                    Novo Operador
                  </PanelActionBtn>
                  <PanelActionBtn
                    size="small"
                    startIcon={<PaidIcon sx={{ fontSize: 14 }} />}
                    onClick={handleOperatorFeeModal}
                  >
                    Taxa de Serviço
                  </PanelActionBtn>
                </Box>
              </SectionBar>

              <PanelRow
                sx={{
                  backgroundColor: alpha(theme.palette.primary.main, 0.02),
                }}
              >
                <RowLabel>Total</RowLabel>
                <RowValue>
                  {deliveryOperators.length}{" "}
                  {deliveryOperators.length === 1
                    ? "operador"
                    : "operadores"}{" "}
                  cadastrados
                </RowValue>
              </PanelRow>
            </Panel>

          </motion.div>
        )}

        {/* ── Operator list panel ────────────────────────────────── */}
        <Panel>
          <SectionBar>
            <SectionBarLabel sx={{ display: "flex", alignItems: "center" }}>
              <PeopleAltIcon sx={{ fontSize: 14, mr: 0.5 }} />
              Operadores
              <Box
                component="span"
                sx={{
                  ml: 0.75,
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.dark,
                  borderRadius: 1,
                  px: 0.75,
                  py: 0.125,
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  minWidth: 20,
                  textAlign: "center",
                }}
              >
                {filteredOperators.length}
              </Box>
            </SectionBarLabel>

            <TextField
              size="small"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{
                        fontSize: 16,
                        color: theme.palette.text.disabled,
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                maxWidth: 180,
                "& .MuiOutlinedInput-root": {
                  height: 28,
                  fontSize: "0.75rem",
                  borderRadius: 1,
                  backgroundColor: theme.palette.background.paper,
                  "& fieldset": {
                    borderColor: theme.palette.divider,
                  },
                },
              }}
            />
          </SectionBar>

          <Box
            sx={{
              maxHeight: 400,
              overflowY: "auto",
              p: 1.5,
            }}
          >
            {loading ? (
              <Box>
                {[1, 2, 3].map((item) => (
                  <Skeleton
                    key={item}
                    variant="rectangular"
                    height={56}
                    sx={{ borderRadius: 1.5, mb: 1.5 }}
                  />
                ))}
              </Box>
            ) : (
              <AnimatePresence>
                {filteredOperators.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.15 }}
                  >
                    <LightGreyBox
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        py: 6,
                        px: 2,
                        border: `1px dashed ${theme.palette.divider}`,
                      }}
                    >
                      <PeopleAltIcon
                        sx={{
                          fontSize: 48,
                          color: alpha(theme.palette.text.secondary, 0.25),
                          mb: 1.5,
                        }}
                      />
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        color="textSecondary"
                        align="center"
                        gutterBottom
                      >
                        {searchTerm
                          ? "Nenhum operador encontrado"
                          : "Nenhum operador cadastrado"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        align="center"
                        sx={{ mb: 2, maxWidth: 320 }}
                      >
                        {searchTerm
                          ? `Sem resultados para "${searchTerm}"`
                          : "Cadastre operadores para gerenciar a sua equipe"}
                      </Typography>
                      {searchTerm ? (
                        <PanelActionBtn onClick={() => setSearchTerm("")}>
                          Limpar Busca
                        </PanelActionBtn>
                      ) : (
                        <PanelActionBtn
                          startIcon={<PersonAddIcon sx={{ fontSize: 14 }} />}
                          onClick={handleCreateOperatorModal}
                        >
                          Adicionar Operador
                        </PanelActionBtn>
                      )}
                    </LightGreyBox>
                  </motion.div>
                ) : (
                  <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    {renderedOperators}
                  </motion.div>
                )}
              </AnimatePresence>
            )}
          </Box>
        </Panel>

        {/* ── Sub-modals ─────────────────────────────────────────── */}
        <RemoveModal
          title="Remover Operador"
          message={`Tem certeza que deseja remover ${
            operatorToDelete?.first_name ?? ""
          } ${operatorToDelete?.last_name ?? ""} da sua equipe?`}
          show={showDeleteModal}
          toggleModal={handleDeleteOperatorModal}
          onRemove={handleDeleteOperator}
        />

        {showCreateOperatorModal && (
          <CreateOrEditDeliveryOperatorModal
            show={showCreateOperatorModal}
            closeModal={handleCreateOperatorModal}
            createOperator={createDeliveryOperator}
            editOperator={editDeliveryOperator}
            operator={editingOperator}
          />
        )}

        <OperatorFeeModal
          show={showOperatorFeeModal}
          closeModal={handleOperatorFeeModal}
          saloonFee={saloonFee}
          counterFee={counterFee}
        />
      </ModalContent>
    </AnimatedModal>
  );
}
